import styled, { css } from 'styled-components';
import { themeGet } from '../../utils/theme-get.js';
import sx from '../../sx.js';
import Textarea from '../Textarea/Textarea.js';

/* eslint-disable @typescript-eslint/no-shadow */

// eslint-disable-next-line @typescript-eslint/no-explicit-any

// border
const disabledBorder = themeGet('colors.border.disabled');
const invalidBorderColor = themeGet('colors.border.danger');
const defaultBorderColor = themeGet('colors.border.input');
const subtleBorderColorHover = themeGet('colors.border.input');
const defaultBorderColorFocus = themeGet('colors.border.focused');

// background
const disabledBackground = themeGet('colors.background.disabled');
const defaultBackgroundColor = themeGet('colors.background.input.default');
const defaultBackgroundColorHover = themeGet('colors.background.input.hovered');
const defaultBackgroundColorFocus = themeGet('colors.background.input.pressed');

// text
const placeholderTextColor = themeGet('colors.text.subtlest');
const disabledTextColor = themeGet('colors.text.disabled');
const transparent = 'transparent';
const disabledRules = {
  backgroundColor: disabledBackground,
  backgroundColorFocus: disabledBackground,
  backgroundColorHover: disabledBackground,
  borderColor: disabledBorder,
  borderColorFocus: defaultBorderColorFocus,
  textColor: disabledTextColor
};
const invalidRules = {
  borderColor: invalidBorderColor,
  borderColorFocus: defaultBorderColorFocus,
  backgroundColor: defaultBackgroundColor,
  backgroundColorFocus: defaultBackgroundColorFocus,
  backgroundColorHover: defaultBackgroundColorHover
};
const backgroundColor = {
  standard: defaultBackgroundColor,
  subtle: transparent,
  none: transparent
};
const backgroundColorFocus = {
  standard: defaultBackgroundColorFocus,
  subtle: defaultBackgroundColorFocus,
  none: transparent
};
const backgroundColorHover = {
  standard: defaultBackgroundColorHover,
  subtle: defaultBackgroundColorHover,
  none: transparent
};
const borderColor = {
  standard: defaultBorderColor,
  subtle: transparent,
  none: transparent
};
const borderColorFocus = {
  standard: defaultBorderColorFocus,
  subtle: defaultBorderColorFocus,
  none: transparent
};
const borderColorHover = {
  standard: defaultBorderColor,
  subtle: subtleBorderColorHover,
  none: transparent
};
const renderResizeStyle = resize => {
  if (resize === 'horizontal') {
    return css(["resize:horizontal;"]);
  }
  if (resize === 'vertical') {
    return css(["resize:vertical;"]);
  }
  if (resize === 'auto') {
    return css(["resize:both;"]);
  }
  return css(["resize:none;"]);
};
const gridSize = () => 8;
const fontSize = () => 14;
const grid = gridSize();
const lineHeightBase = grid * 2.5;
const lineHeightCompact = grid * 2;
const compactVerticalPadding = 2;
const verticalPadding = 6;
const horizontalPadding = grid;
const transitionDuration = '0.2s';
/** We are using the feature-flagged border-width bc this will land soon anyway */
const borderWidth = 1;
const borderHeight = borderWidth;
const horizontalPaddingWithoutBorderWidth = horizontalPadding - borderWidth;
const borderBoxMinHeight = (minimumRows, borderHeight) => {
  const contentHeight = lineHeightBase * minimumRows;
  return contentHeight + verticalPadding * 2 + borderHeight * 2;
};
const borderBoxMinHeightCompact = (minimumRows, borderHeight) => {
  const contentHeightCompact = lineHeightCompact * minimumRows;
  return contentHeightCompact + compactVerticalPadding * 2 + borderHeight * 2;
};
const StyledTextarea = styled(Textarea).withConfig({
  displayName: "StyledTextarea",
  componentId: "sc-cvgtes-0"
})(["font-size:inherit;font-family:inherit;background-color:transparent;-webkit-appearance:none;color:inherit;width:100%;display:block;box-sizing:border-box;width:100%;min-width:0;max-width:100%;height:auto;margin:0;position:relative;flex:1 1 100%;border-radius:", ";border-width:1px;font-size:", "px;outline:none;overflow:auto;transition:background-color ", " ease-in-out,border-color ", " ease-in-out;word-wrap:break-word;&:disabled{color:", ";cursor:not-allowed;-webkit-text-fill-color:unset;-webkit-opacity:1;opacity:1;}&:invalid{box-shadow:none;}border-style:", ";border-color:", ";font-family:", ";padding:", ";&[data-compact]{min-height:", ";padding:", "px ", "px;line-height:", " / ", ";}&:not([data-compact]){min-height:", ";padding:", "px ", "px;line-height:", ";}&:focus{background-color:", ";border-color:", ";box-shadow:inset 0 0 0 1px ", ";}&:not(:focus){background-color:", ";border-color:", ";}&::placeholder{color:", ";font-family:", ";}&:disabled::placeholder{color:", ";}&[data-invalid]:focus{background-color:", ";border-color:", ";box-shadow:inset 0 0 0 1px ", ";}&[data-invalid]:not(:focus){background-color:", ";border-color:", ";box-shadow:inset 0 0 0 1px ", ";}&:disabled:focus{background-color:", ";border-color:", ";}&:disabled:not(:focus){background-color:", ";border-color:", ";}&:hover:not(:read-only):not(:focus){background-color:", ";border-color:", ";&:disabled{background-color:", ";}&[data-invalid]{background-color:", ";border-color:", ";box-shadow:inset 0 0 0 1px ", ";}}", " ", " ", ""], themeGet('radii.1'), fontSize, transitionDuration, transitionDuration, themeGet('colors.text.disabled'), p => p.appearance === 'none' ? 'none' : 'solid', p => p.appearance ? borderColor[p.appearance] : themeGet('colors.border.input'), p => p.isMonospaced ? themeGet('fontFamily.mono') : themeGet('fontFamily.normal'), p => p.isCompact ? `${compactVerticalPadding}px ${horizontalPadding - borderWidth}px` : `${verticalPadding}px ${horizontalPadding - borderWidth}px`, p => borderBoxMinHeightCompact(p.rows, borderHeight), compactVerticalPadding, horizontalPadding - borderWidth, lineHeightCompact, fontSize(), p => `${borderBoxMinHeight(p.rows, borderHeight)}px`, verticalPadding, horizontalPaddingWithoutBorderWidth, lineHeightBase / fontSize(), p => p.appearance ? backgroundColorFocus[p.appearance] : '', p => p.appearance ? borderColorFocus[p.appearance] : '', p => p.appearance ? borderColorFocus[p.appearance] : '', p => p.appearance ? backgroundColor[p.appearance] : '', p => p.appearance ? borderColor[p.appearance] : '', placeholderTextColor, p => p.isMonospaced ? themeGet('fontFamily.mono') : themeGet('fontFamily.normal'), disabledRules.textColor, invalidRules.backgroundColorFocus, invalidRules.borderColorFocus, invalidRules.borderColorFocus, invalidRules.backgroundColor, invalidRules.borderColor, invalidRules.borderColor, p => p.appearance === 'standard' ? disabledRules.backgroundColorFocus : '', p => p.appearance === 'standard' ? disabledRules.borderColorFocus : '', p => p.appearance === 'standard' ? disabledRules.backgroundColor : '', p => p.appearance === 'standard' ? disabledRules.borderColor : '', p => p.appearance ? backgroundColorHover[p.appearance] : '', p => p.appearance ? borderColorHover[p.appearance] : '', disabledRules.backgroundColorHover, invalidRules.backgroundColorHover, invalidRules.borderColor, invalidRules.borderColor, p => renderResizeStyle(p.resize), p => p.maxHeight && `max-height: ${p.maxHeight};`, sx);

export { StyledTextarea, borderWidth, defaultBackgroundColor, defaultBackgroundColorFocus, defaultBackgroundColorHover, defaultBorderColor, defaultBorderColorFocus, disabledBackground, disabledBorder, disabledTextColor, fontSize, gridSize, horizontalPadding, invalidBorderColor, placeholderTextColor, subtleBorderColorHover, transparent };
